// ============ THE SUIT GAUGES: NUMBER -> DISPLAY ============
// How a value becomes ten icons. Pure and dependency-free — no DOM, no imports — for the same reason
// hazard-engine.js is: it lets the mapping be unit-tested in node (tests/gauge.test.mjs) instead of only by
// standing on a planet and squinting at 13px of lightning.
//
// This is the whole of the display logic. app.js does the DOM half (build the slots, write a transform, set a
// colour) and asks this module every question that has an answer worth being wrong about.

export const PIP_SLOTS = 10;    // icons in a gauge
export const PIP_STEPS = 10;    // points each icon carries; SLOTS x STEPS = the whole 0..100 range

// The gauge's integer value. Clamped, rounded — and never rounded down to zero while there is anything left.
//
// ALIVE IS NEVER AN EMPTY ROW. Math.round sends 0.4 % to 0, and a gauge showing nothing at all reads as dead, which
// is the one thing it must not say while the player is still standing. The floor of 1 costs nothing anywhere else:
// it can only fire below half a point.
export function pipValue(pct) {
  if (!isFinite(pct)) return 0;
  const raw = Math.max(0, Math.min(100, pct));
  return raw > 0 ? Math.max(1, Math.round(raw)) : 0;
}

// How full each of the ten slots is, 0..1, front to back. Only ever one partial slot: everything before it is 1 and
// everything after is 0, which is what makes the row read as a count rather than a gradient.
export function pipFills(v) {
  const out = new Array(PIP_SLOTS);
  for (let i = 0; i < PIP_SLOTS; i++) out[i] = Math.max(0, Math.min(PIP_STEPS, v - i * PIP_STEPS)) / PIP_STEPS;
  return out;
}

// A fill fraction as the icon's SCALE. sqrt, not the fraction itself, so the icon's AREA tracks the value: area is
// what the eye compares when judging two shapes, and it is what keeps the low end visible — a linear tenth of 13px
// is 1.3px and simply is not there, where sqrt(0.1) is 4.1px and still legibly a heart.
export function pipScale(f) {
  return f >= 1 ? 1 : Math.sqrt(Math.max(0, f));
}

// The CSS transform for a slot. Full slots get NO transform at all rather than scale(1) — an identity transform
// still creates a compositing layer per icon, and there are twenty of them on screen.
export function pipTransform(f) {
  return f >= 1 ? '' : 'scale(' + pipScale(f).toFixed(3) + ')';
}

// GREEN -> AMBER -> RED. Not a constant red: a bar that is always red is a warning light that is always on, and the
// colour has to carry meaning before the count does, because the icons are 13px.
export const HEALTH_GREEN = '#7BD88F', HEALTH_AMBER = '#FFD23B', HEALTH_RED = '#FF4444';
export function healthColour(h) {
  return h > 60 ? HEALTH_GREEN : h > 30 ? HEALTH_AMBER : HEALTH_RED;
}

// THE OXYGEN RESERVE, AS THE PLAYER READS IT: time. "O₂ 4:32" answers "how long can I stay out here" in the unit the
// question is asked in, where a percentage or a row of icons would make the player do the conversion — and there is
// no room in the row for a third ten-icon gauge anyway.
//
// ROUNDED UP, like every countdown, for the same reason pipValue never shows an empty row while anything is left:
// 0:00 has to MEAN empty, because it is the moment the suit starts to suffocate, and a player who is still breathing
// must never be shown it. The tolerance absorbs float dust from the per-tick drain, so a reserve at 60.0000001 s reads
// 1:00 and not 1:01.
export function formatO2(seconds) {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return '--';
  const s = Math.max(0, Math.ceil(seconds - 1e-6));
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}
// The health colours, not energy's, because this is the one reserve whose end is LOSING HEALTH: green while there is
// headroom, amber from the warning line (the engine's HAZARD.O2_LOW_S, passed in so this module stays import-free),
// red at empty, which is exactly when the hearts start to fall.
// The line carries formatO2's allowance for float dust, so the colour turns on the reading that says 1:00.
export function o2Colour(seconds, lowSeconds) {
  return !(seconds > 0) ? HEALTH_RED : seconds <= lowSeconds + 1e-6 ? HEALTH_AMBER : HEALTH_GREEN;
}

// Cyan while there is headroom, warming as it drains — the player should FEEL the limit approaching rather than
// discover it at zero. These read as "resource", not "error", until it is genuinely spent, which is why the low end
// is orange rather than the health red: running out of energy is an inconvenience, running out of health is death.
export const ENERGY_CYAN = '#00FFFF', ENERGY_AMBER = '#FFD23B', ENERGY_LOW = '#FF7043';
export function energyColour(pct) {
  return pct > 55 ? ENERGY_CYAN : pct > 25 ? ENERGY_AMBER : ENERGY_LOW;
}

// MINING ENERGY USED TO BE COMPUTED HERE, as `energyNow(value, at, now, cap)` — value plus elapsed seconds, one
// point per second, clamped to a cap the caller passed in. All three of those are now questions rather than
// constants: BATTERIES move the cap, SOLAR PANELS and the sky move the rate, and a rate that changes while you walk
// into night cannot be solved from a single anchor at all. energy.mjs owns it, in the one copy the client, the
// Cloud Function and the zone server all read.
//
// Deleted rather than left as a wrapper. It was correct-looking, dependency-free and tested, which is exactly what
// makes a stale duplicate dangerous: the next caller to reach for it would have got the old flat model back with no
// error anywhere. What stays here is the part that is genuinely about DISPLAY — a number becoming ten icons.
